import React, { useState, lazy } from 'react';
import { Check, Lock, Shield, Mail } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
export type ComboId = 1 | 2 | 3;
interface Feature {
  text: string;
  type: 'manual' | 'exclusive' | 'bonus' | 'info';
}
interface Combo {
  id: ComboId;
  title: string;
  subtitle: string;
  price: string;
  originalPrice: string;
  discount: string;
  isRecommended?: boolean;
  features: Feature[];
}
const combos: Combo[] = [{
  id: 2,
  title: 'COMBO 2: EL MÁS ELEGIDO',
  subtitle: 'El equilibrio perfecto entre detección e influencia.',
  price: '29.990',
  originalPrice: '55.000',
  discount: '55%',
  isRecommended: true,
  features: [{
    text: 'Manual Principal: Lenguaje Corporal',
    type: 'manual'
  }, {
    text: 'Manual 2: Carisma y Presencia',
    type: 'exclusive'
  }, {
    text: 'Bonus: Lectura Rápida',
    type: 'bonus'
  }, {
    text: 'Bonus: Influencia y Persuasión',
    type: 'bonus'
  }, {
    text: 'Bonus: Detector de Mentiras',
    type: 'bonus'
  }, {
    text: 'Material digital (PDF) — enviado por mail',
    type: 'info'
  }]
}, {
  id: 1,
  title: 'COMBO 1: SISTEMA BASE',
  subtitle: 'Para iniciarse en la lectura de comportamiento.',
  price: '19.990',
  originalPrice: '35.000',
  discount: '45%',
  features: [{
    text: 'Manual Principal: Lenguaje Corporal',
    type: 'manual'
  }, {
    text: 'Bonus: Lectura Rápida',
    type: 'bonus'
  }, {
    text: 'Bonus: Influencia y Persuasión',
    type: 'bonus'
  }, {
    text: 'Bonus: Detector de Mentiras',
    type: 'bonus'
  }, {
    text: 'Material digital (PDF) — enviado por mail',
    type: 'info'
  }]
}, {
  id: 3,
  title: 'COMBO 3: NIVEL AVANZADO',
  subtitle: 'Dominio total de la estrategia social.',
  price: '39.990',
  originalPrice: '80.000',
  discount: '60%',
  features: [{
    text: 'Manual Principal: Lenguaje Corporal',
    type: 'manual'
  }, {
    text: 'Manual 2: Carisma y Presencia',
    type: 'exclusive'
  }, {
    text: 'Manual 3: Psicología Oscura',
    type: 'exclusive'
  }, {
    text: 'Bonus: Lectura Rápida',
    type: 'bonus'
  }, {
    text: 'Bonus: Influencia y Persuasión',
    type: 'bonus'
  }, {
    text: 'Bonus: Detector de Mentiras',
    type: 'bonus'
  }, {
    text: 'Material digital (PDF) — enviado por mail',
    type: 'info'
  }]
}];
interface ComboSelectorProps {
  onBuy: (comboId: ComboId) => void;
}
export function ComboSelector({
  onBuy
}: ComboSelectorProps) {
  const [selectedId, setSelectedId] = useState<ComboId>(2);
  return <div className="w-full max-w-3xl mx-auto" data-id="element-24">
      {/* Mercado Pago Trust Badge */}
      <div className="bg-white rounded-lg px-4 py-2 flex items-center justify-center gap-3 mb-4" data-id="element-25">
        <Lock className="w-3.5 h-3.5 text-gray-600 shrink-0" data-id="element-26" />
        <span className="text-gray-700 text-xs font-medium whitespace-nowrap" data-id="element-27">
          Pago seguro a través de
        </span>
        <img src="https://cdn.magicpatterns.com/uploads/h6RD2QoSCEaMoJX8bF7orb/pasted-image.png" alt="Mercado Pago" loading="lazy" width={100} height={28} className="h-6 object-contain shrink-0" data-id="element-28" />
      </div>

      <div className="border border-gray-700 rounded-xl overflow-hidden bg-gray-900/80 shadow-2xl" data-id="element-29">
        {combos.map((combo, index) => {
        const isSelected = selectedId === combo.id;
        const isLast = index === combos.length - 1;
        return <div key={combo.id} data-id="element-30">
              <button onClick={() => setSelectedId(combo.id)} className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors duration-150 relative ${isSelected ? 'bg-gray-800/60' : 'bg-transparent hover:bg-gray-800/30'} ${!isLast && !isSelected ? 'border-b border-gray-800' : ''}`} data-id="element-31">
                {/* Radio */}
                <div className="shrink-0" data-id="element-32">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-yellow-500 bg-yellow-500' : 'border-gray-600 bg-transparent'}`} data-id="element-33">
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-black" data-id="element-34" />}
                  </div>
                </div>

                {/* Title + Subtitle */}
                <div className="flex-1 min-w-0" data-id="element-35">
                  <h3 className={`font-extrabold text-sm md:text-base uppercase tracking-wide leading-tight ${isSelected ? 'text-yellow-400' : 'text-white'}`} data-id="element-36">
                    {combo.title}
                  </h3>
                  <p className="text-gray-500 text-xs mt-0.5 italic leading-tight" data-id="element-37">
                    {combo.subtitle}
                  </p>
                </div>

                {/* Price + Badge */}
                <div className="shrink-0 text-right flex flex-col items-end" data-id="element-38">
                  {combo.isRecommended && <span className="bg-yellow-500 text-black text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded whitespace-nowrap mb-0.5" data-id="element-39">
                      Más Vendido
                    </span>}
                  <div className="flex items-center gap-1.5" data-id="element-40">
                    <span className="text-gray-500 text-xs line-through" data-id="element-41">
                      ${combo.originalPrice}
                    </span>
                    <span className="bg-red-600 text-white text-[9px] font-bold px-1 py-0.5 rounded leading-none" data-id="element-42">
                      {combo.discount}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-0.5" data-id="element-43">
                    <span className="text-gray-400 text-[10px] font-medium" data-id="element-44">
                      ARS
                    </span>
                    <span className="text-white text-xl md:text-2xl font-extrabold leading-none" data-id="element-45">
                      {combo.price}
                    </span>
                  </div>
                </div>
              </button>

              {/* Expanded Features */}
              <AnimatePresence data-id="element-46">
                {isSelected && <m.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-47">
                    <div className={`px-4 pb-3 ${!isLast ? 'border-b border-gray-800' : ''}`} data-id="element-48">
                      <div className="ml-7" data-id="element-49">
                        <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-2" data-id="element-50">
                          Incluye:
                        </p>
                        <ul className="space-y-1.5" data-id="element-51">
                          {combo.features.map((feature, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-52">
                              <Check className={`w-3.5 h-3.5 shrink-0 mt-px ${feature.type === 'exclusive' ? 'text-yellow-400' : feature.type === 'bonus' ? 'text-green-400' : feature.type === 'info' ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-53" />
                              <span className="text-xs leading-snug" data-id="element-54">
                                {feature.type === 'manual' && <span className="text-gray-300" data-id="element-55">
                                    {feature.text}
                                  </span>}
                                {feature.type === 'exclusive' && <span className="text-yellow-400 font-bold" data-id="element-56">
                                    {feature.text}
                                  </span>}
                                {feature.type === 'bonus' && <span className="text-gray-300" data-id="element-57">
                                    {feature.text}{' '}
                                    <span className="text-green-400 font-bold" data-id="element-58">
                                      (Gratis)
                                    </span>
                                  </span>}
                                {feature.type === 'info' && <span className="text-gray-500 italic" data-id="element-59">
                                    📄 {feature.text}
                                  </span>}
                              </span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                  </m.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>

      {/* CTA Button */}
      <div className="mt-4" data-id="element-60">
        <button onClick={() => onBuy(selectedId)} className="w-full py-3.5 px-6 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black font-extrabold text-base md:text-lg uppercase tracking-wider transition-all duration-200 shadow-[0_0_25px_rgba(253,185,19,0.3)] hover:shadow-[0_0_35px_rgba(253,185,19,0.5)]" data-id="element-61">
          Comprar Ahora
        </button>
      </div>

      {/* Trust Badges */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-gray-500 text-[11px]" data-id="element-62">
        <span className="flex items-center gap-1" data-id="element-63">
          <Lock className="w-3 h-3" data-id="element-64" /> Pago 100% Seguro
        </span>
        <span className="flex items-center gap-1" data-id="element-65">
          <Check className="w-3 h-3" data-id="element-66" /> Acceso Inmediato
        </span>
        <span className="flex items-center gap-1" data-id="element-67">
          <Mail className="w-3 h-3" data-id="element-68" /> PDF por email
        </span>
        <span className="flex items-center gap-1" data-id="element-69">
          <Shield className="w-3 h-3" data-id="element-70" /> Garantía 7 días
        </span>
      </div>
    </div>;
}